import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthProvider, AuthTokens, AuthUser, Me } from "@/api/types";

export type SessionStatus = "guest" | "member";

/** 온보딩 플래그. 로그인 응답 user 또는 GET /me flags에서 온다. */
export type OnboardingFlags = {
  consentsRequired: boolean;
  birthDateRequired: boolean;
  profileRequired: boolean;
};

/** publicId = 회원번호(8자, AC0-96). GET /me 가 준다. 로그인 응답에는 없어 첫 /me 전까지 null. */
type UserSummary = { id: string; publicId: string | null; isNew: boolean } & OnboardingFlags;

export type AccountSummary = { displayName: string | null; photoUrl: string | null };

type AuthState = {
  /** 앱(Bearer)만. 메모리 전용 — persist 제외. 웹은 항상 null(쿠키). */
  accessToken: string | null;
  status: SessionStatus;
  user: UserSummary | null;
  settings: Me["settings"] | null;
  balance: number | null;
  /** 계정 닉네임·사진(spec 7절). GET /me·PATCH /me 응답으로 갱신. 마이페이지·GNB 오프라인 표시용 캐시. */
  account: AccountSummary | null;
  /** 기본 프로필 요약(마이페이지 오프라인 표시용 캐시). */
  profile: Me["profile"];
  /** 1회성 공지(0.5절). GET /me마다 교체, persist 제외 — 표시 여부는 uiStore.shownNotices. */
  notices: Me["notices"];
  /** 마지막 로그인 제공자(설정 "로그인 계정" suffix). 서버가 Me.user.provider를 주면 그것으로 교체한다. */
  provider: AuthProvider | null;
  setAccess: (token: string | null) => void;
  /** Bearer 모드 로그인·refresh 응답의 tokens. refresh 토큰 저장(secureStore)은 호출부가 한다. */
  setSession: (tokens: AuthTokens) => void;
  /** 로그인 응답 user(모드 공통) + 어느 제공자로 로그인했는지. */
  setLoginUser: (user: AuthUser, provider?: AuthProvider) => void;
  /** GET /me 성공 → member. */
  setMember: (me: Me) => void;
  setBalance: (balance: number) => void;
  /** PATCH /me 응답 등으로 계정 닉네임·사진 갱신. */
  setAccount: (patch: Partial<AccountSummary>) => void;
  /** 온보딩 단계 완료(약관·생년월일·프로필) 또는 게이트 403 수신 시 플래그 갱신. */
  setFlags: (flags: Partial<OnboardingFlags>) => void;
  clear: () => void;
};

const initial = {
  accessToken: null,
  status: "guest" as SessionStatus,
  user: null,
  settings: null,
  balance: null,
  provider: null,
  account: null as AccountSummary | null,
  profile: null as Me["profile"],
  notices: [] as Me["notices"],
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initial,
      setAccess: (accessToken) => set({ accessToken }),
      setSession: ({ accessToken }) => set({ accessToken }),
      setLoginUser: (user, provider) =>
        set((s) => ({
          status: "member",
          provider: provider ?? s.provider,
          user: {
            id: user.id,
            publicId: s.user?.id === user.id ? s.user.publicId : null,
            isNew: user.isNew,
            consentsRequired: user.consentsRequired,
            birthDateRequired: user.birthDateRequired,
            profileRequired: user.profileRequired,
          },
        })),
      setMember: (me) =>
        set((s) => ({
          status: "member",
          user: {
            id: me.user?.id ?? s.user?.id ?? "",
            publicId: me.user?.publicId ?? s.user?.publicId ?? null,
            isNew: s.user?.isNew ?? false,
            consentsRequired: me.flags?.consentsRequired ?? false,
            birthDateRequired: me.flags?.birthDateRequired ?? false,
            profileRequired: me.flags?.profileRequired ?? false,
          },
          settings: me.settings ?? s.settings,
          balance: me.bricks?.balance ?? s.balance,
          account: me.user ? { displayName: me.user.displayName, photoUrl: me.user.photoUrl } : s.account,
          profile: me.profile ?? null,
          notices: me.notices ?? [],
        })),
      setBalance: (balance) => set({ balance }),
      setAccount: (patch) => set((s) => ({ account: { displayName: null, photoUrl: null, ...s.account, ...patch } })),
      setFlags: (flags) => set((s) => (s.user ? { user: { ...s.user, ...flags } } : s)),
      clear: () => set({ ...initial }),
    }),
    {
      name: "hamlak.auth",
      // accessToken은 메모리에만 둔다(0.3절). 나머지는 오프라인 부팅용 캐시.
      partialize: (s) => ({ status: s.status, user: s.user, settings: s.settings, balance: s.balance, provider: s.provider, account: s.account, profile: s.profile }),
    },
  ),
);

export const selectIsMember = (s: AuthState) => s.status === "member";
